import { useFlowBuilder } from 'hooks/useFlowBuilder';
import React, { createContext, FC, PropsWithChildren, useContext, useState } from 'react';
import { useNotification } from '@wix/gg-components';
import { OrchestratorProvider, StepDef } from '@wix/gg-components/orchestrator';
import { windowUtils } from 'utils/window';
import { TARGET_STEP_PARAM } from 'config/consts';
import { ActionId, StepId } from 'config/steps';
import { biEvents } from 'api/bi-events';
import { BI_STEP_NAME } from 'config/bi';
import { useAppStore } from 'stores/appStore';

type Value = {
  steps: StepDef[];
};

const Context = createContext<Value>({} as Value);

export const useFlowContext = () => useContext(Context);

export const FlowContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const [targetStepId] = useState(() => windowUtils.extractUrlParam<StepId | ActionId>(TARGET_STEP_PARAM));
  const { currentStep } = useAppStore.getState();
  const flow = useFlowBuilder();
  const { promptNotification } = useNotification();

  const targetStep = targetStepId || currentStep;
  const steps = flow.filter((step) => step.type === 'step');
  const initialIndex = flow.findIndex((step) => step.id === targetStep);

  return (
    <Context.Provider value={{ steps }}>
      <OrchestratorProvider
        initialIndex={initialIndex >= 0 ? initialIndex : 0}
        flow={flow}
        onStepChange={(step) => {
          useAppStore.setState({ currentStep: step?.id as StepId });
          step && biEvents.stepViewed({ pageName: BI_STEP_NAME[step.id as StepId] });
        }}
        onError={(error) => {
          promptNotification({ message: error, status: 'error' });
        }}
      >
        {children}
      </OrchestratorProvider>
    </Context.Provider>
  );
};
